import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MOBILE_BREAKPOINT } from '@/lib/constants';

/**
 * Unified Content View Management System
 * 
 * This store uses a discriminated union type (MainContentView) to manage what's
 * displayed in the main content area. This architecture prevents impossible states
 * and ensures clean view transitions.
 * 
 * Benefits:
 * - **Type Safety**: TypeScript enforces that only valid view states exist
 * - **No Impossible States**: Can't have threadInbox AND manageSections open simultaneously
 * - **Single Source of Truth**: mainContentView is the authoritative state
 * - **Cleaner Code**: One function call to switch views instead of multiple
 * 
 * Example Usage:
 * ```typescript
 * // OLD WAY (error-prone):
 * clearSelection();
 * closeThreadInboxView();
 * closeManageSectionsView();
 * 
 * // NEW WAY (bulletproof):
 * showWelcomeScreen();
 * ```
 * 
 * @see MainContentView for all possible view states
 */

/**
 * Unified type for main content area view state.
 * Using a discriminated union prevents impossible states like
 * having both threadInboxView and manageSectionsView true.
 * 
 * Legacy boolean properties (manageSectionsView, threadInboxView) are automatically
 * synced for backwards compatibility during migration period.
 */
export type MainContentView =
  | { type: 'welcome' }
  | { type: 'manageSections' }
  | { type: 'threadInbox' }
  | { type: 'browse'; tab: 'channels' | 'people' }
  | { type: 'decisions' }
  | { type: 'workItems' }
  | { type: 'channel'; channelId: string }
  | { type: 'dm'; dmId: string };

interface UIState {
  // Modal States
  commandPaletteOpen: boolean;
  createChannelOpen: boolean;
  invitePeopleOpen: boolean;
  shareDialogOpen: boolean;
  focusSetupOpen: boolean;
  workspaceSelectorOpen: boolean;

  // Main Content View (unified state)
  mainContentView: MainContentView;

  // Legacy view states (DEPRECATED - use mainContentView instead)
  // Kept temporarily for backwards compatibility during migration
  manageSectionsView: boolean;
  threadInboxView: boolean;

  // Message Context
  highlightedMessageId: string | null;
  selectedTopicFilter: string | null;
  selectedThreadId: string | null;

  // Per-channel UI state (session-only)
  channelScrollPositions: Record<string, number>;

  // Panel States (persisted)
  sidebarCollapsed: boolean;
  threadPanelWidth: number;
  topicPanelCollapsed: boolean;

  // Mobile States
  isMobile: boolean;
  mobileSidebarOpen: boolean;

  // Actions - Modals
  openCommandPalette: () => void;
  closeCommandPalette: () => void;
  toggleCommandPalette: () => void;

  openCreateChannel: () => void;
  closeCreateChannel: () => void;

  openInvitePeople: () => void;
  closeInvitePeople: () => void;

  openShareDialog: () => void;
  closeShareDialog: () => void;

  openFocusSetup: () => void;
  closeFocusSetup: () => void;

  openWorkspaceSelector: () => void;
  closeWorkspaceSelector: () => void;

  // Actions - View States (NEW UNIFIED API)
  setMainContentView: (view: MainContentView) => void;
  showWelcomeScreen: () => void;
  showManageSections: () => void;
  showThreadInbox: () => void;
  showBrowse: (tab?: 'channels' | 'people') => void;
  showDecisions: () => void;
  showWorkItems: () => void;
  showChannel: (channelId: string) => void;
  showDM: (dmId: string) => void;

  // Legacy Actions (DEPRECATED - use setMainContentView or helper methods)
  openManageSectionsView: () => void;
  closeManageSectionsView: () => void;
  openThreadInboxView: () => void;
  closeThreadInboxView: () => void;

  // Actions - Message Context
  setHighlightedMessage: (messageId: string | null) => void;
  clearHighlightedMessage: () => void;
  setTopicFilter: (topic: string | null) => void;
  setThreadId: (threadId: string | null) => void;
  clearMessageContext: () => void;

  // Actions - Per-channel UI state
  setChannelScrollPosition: (channelId: string, scrollTop: number) => void;
  getChannelScrollPosition: (channelId: string) => number | undefined;
  clearChannelScrollPosition: (channelId: string) => void;

  // Actions - Panel States
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setThreadPanelWidth: (width: number) => void;
  toggleTopicPanel: () => void;
  setTopicPanelCollapsed: (collapsed: boolean) => void;

  // Actions - Mobile
  setIsMobile: (isMobile: boolean) => void;
  toggleMobileSidebar: () => void;
  setMobileSidebarOpen: (open: boolean) => void;

  // Cleanup
  reset: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial State
      commandPaletteOpen: false,
      createChannelOpen: false,
      invitePeopleOpen: false,
      shareDialogOpen: false,
      focusSetupOpen: false,
      workspaceSelectorOpen: false,

      mainContentView: { type: 'welcome' },

      manageSectionsView: false,
      threadInboxView: false,

      highlightedMessageId: null,
      selectedTopicFilter: null,
      selectedThreadId: null,

      channelScrollPositions: {},

      sidebarCollapsed: false,
      threadPanelWidth: 384,
      topicPanelCollapsed: false,

      isMobile: typeof window !== 'undefined' ? window.innerWidth < MOBILE_BREAKPOINT : false,
      mobileSidebarOpen: false,

      // Modal Actions
      openCommandPalette: () => set({ commandPaletteOpen: true }),
      closeCommandPalette: () => set({ commandPaletteOpen: false }),
      toggleCommandPalette: () => set(state => ({ commandPaletteOpen: !state.commandPaletteOpen })),

      openCreateChannel: () => set({ createChannelOpen: true }),
      closeCreateChannel: () => set({ createChannelOpen: false }),

      openInvitePeople: () => set({ invitePeopleOpen: true }),
      closeInvitePeople: () => set({ invitePeopleOpen: false }),

      openShareDialog: () => set({ shareDialogOpen: true }),
      closeShareDialog: () => set({ shareDialogOpen: false }),

      openFocusSetup: () => set({ focusSetupOpen: true }),
      closeFocusSetup: () => set({ focusSetupOpen: false }),

      openWorkspaceSelector: () => set({ workspaceSelectorOpen: true }),
      closeWorkspaceSelector: () => set({ workspaceSelectorOpen: false }),

      // View State Actions (NEW UNIFIED API)
      setMainContentView: (view: MainContentView) => {
        // Update unified state
        set({ mainContentView: view });
        
        // Also update legacy states for backwards compatibility
        set({
          manageSectionsView: view.type === 'manageSections',
          threadInboxView: view.type === 'threadInbox',
        });
      },

      showWelcomeScreen: () => {
        get().setMainContentView({ type: 'welcome' });
      },

      showManageSections: () => {
        get().setMainContentView({ type: 'manageSections' });
      },

      showThreadInbox: () => {
        get().setMainContentView({ type: 'threadInbox' });
      },

      showBrowse: (tab = 'channels') => {
        get().setMainContentView({ type: 'browse', tab });
      },

      showDecisions: () => {
        get().setMainContentView({ type: 'decisions' });
      },

      showWorkItems: () => {
        get().setMainContentView({ type: 'workItems' });
      },

      showChannel: (channelId: string) => {
        get().setMainContentView({ type: 'channel', channelId });
      },

      showDM: (dmId: string) => {
        get().setMainContentView({ type: 'dm', dmId });
      },

      // Legacy Actions (DEPRECATED - maintained for backwards compatibility)
      openManageSectionsView: () => {
        get().setMainContentView({ type: 'manageSections' });
      },
      
      closeManageSectionsView: () => {
        // Only close if currently showing manage sections
        const current = get().mainContentView;
        if (current.type === 'manageSections') {
          get().setMainContentView({ type: 'welcome' });
        }
      },

      openThreadInboxView: () => {
        get().setMainContentView({ type: 'threadInbox' });
      },
      
      closeThreadInboxView: () => {
        // Only close if currently showing thread inbox
        const current = get().mainContentView;
        if (current.type === 'threadInbox') {
          get().setMainContentView({ type: 'welcome' });
        }
      },

      // Message Context Actions
      setHighlightedMessage: (messageId: string | null) => set({ highlightedMessageId: messageId }),
      clearHighlightedMessage: () => set({ highlightedMessageId: null }),

      setTopicFilter: (topic: string | null) => set({ selectedTopicFilter: topic }),

      setThreadId: (threadId: string | null) => set({ selectedThreadId: threadId }),

      clearMessageContext: () => set({
        highlightedMessageId: null,
        selectedTopicFilter: null,
        selectedThreadId: null,
      }),

      // Per-channel UI state
      setChannelScrollPosition: (channelId: string, scrollTop: number) =>
        set(state => ({
          channelScrollPositions: {
            ...state.channelScrollPositions,
            [channelId]: scrollTop,
          },
        })),

      getChannelScrollPosition: (channelId: string) => {
        return get().channelScrollPositions[channelId];
      },

      clearChannelScrollPosition: (channelId: string) =>
        set(state => {
          const next = { ...state.channelScrollPositions };
          delete next[channelId];
          return { channelScrollPositions: next };
        }),

      // Panel State Actions
      toggleSidebar: () => set(state => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed: boolean) => set({ sidebarCollapsed: collapsed }),

      setThreadPanelWidth: (width: number) => {
        // Clamp width between 280 and 800
        const clampedWidth = Math.min(Math.max(width, 280), 800);
        set({ threadPanelWidth: clampedWidth });
      },

      toggleTopicPanel: () => set(state => ({ topicPanelCollapsed: !state.topicPanelCollapsed })),
      setTopicPanelCollapsed: (collapsed: boolean) => set({ topicPanelCollapsed: collapsed }),

      // Mobile Actions
      setIsMobile: (isMobile: boolean) => set({ isMobile }),
      toggleMobileSidebar: () => set(state => ({ mobileSidebarOpen: !state.mobileSidebarOpen })),
      setMobileSidebarOpen: (open: boolean) => set({ mobileSidebarOpen: open }),

      // Cleanup
      reset: () => set({
        commandPaletteOpen: false,
        createChannelOpen: false,
        invitePeopleOpen: false,
        shareDialogOpen: false,
        focusSetupOpen: false,
        workspaceSelectorOpen: false,
        mainContentView: { type: 'welcome' },
        manageSectionsView: false,
        threadInboxView: false,
        highlightedMessageId: null,
        selectedTopicFilter: null,
        selectedThreadId: null,
        channelScrollPositions: {},
        // Don't reset persisted panel states
        isMobile: false,
        mobileSidebarOpen: false,
      }),
    }),
    {
      name: 'cleariest-ui-state',
      // Only persist UI preferences, not modal/context states
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        threadPanelWidth: state.threadPanelWidth,
        topicPanelCollapsed: state.topicPanelCollapsed,
        channelScrollPositions: state.channelScrollPositions,
      }),
      storage: createJSONStorage(() => localStorage),
      // Merge function validates persisted values DURING hydration (before render)
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<UIState> | undefined;
        
        // Validate threadPanelWidth - must be between 280-800, default to 384
        let validatedThreadPanelWidth = currentState.threadPanelWidth;
        if (persisted?.threadPanelWidth !== undefined) {
          const width = persisted.threadPanelWidth;
          if (typeof width === 'number' && !isNaN(width) && width >= 280 && width <= 800) {
            validatedThreadPanelWidth = width;
          } else {
            console.warn('[useUIStore] Invalid threadPanelWidth in storage, using default:', width);
            validatedThreadPanelWidth = 384;
          }
        }
        
        return {
          ...currentState,
          ...persisted,
          threadPanelWidth: validatedThreadPanelWidth,
        };
      },
    }
  )
);

// Keep isMobile in sync with window resize
if (typeof window !== 'undefined') {
  window.addEventListener('resize', () => {
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (useUIStore.getState().isMobile !== isMobile) {
      useUIStore.getState().setIsMobile(isMobile);
    }
  });
}
